/**
 * AgentSignUpForm - Dedicated signup form for individual agents
 * Creates organization + fma_basic subscription + agent tag
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, Users, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { callFunctionUnauthenticated } from "@/lib/http";

interface AgentSignupData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

interface AgentSignUpFormProps {
  onClose?: () => void;
  redirectAfterSignup?: boolean;
  variant?: "default" | "dark";
  onSwitchToLogin?: () => void;
}

export default function AgentSignUpForm({
  onClose,
  redirectAfterSignup = true,
  variant = "default",
  onSwitchToLogin,
}: AgentSignUpFormProps) {
  const [formData, setFormData] = useState<AgentSignupData>({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Partial<AgentSignupData>>({});

  const navigate = useNavigate();
  const { toast } = useToast();

  const validateForm = (): boolean => {
    const newErrors: Partial<AgentSignupData> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof AgentSignupData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);
    try {
      let result: { ok: boolean };

      // Use production edge function
      result = await callFunctionUnauthenticated("agent_signup", {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
      });

      const successMessage =
        "Your agent account has been created successfully. Please check your email to verify your account.";

      toast({
        title: "Welcome to Find My Agency!",
        description: successMessage,
      });

      // Close modal if provided
      if (onClose) {
        onClose();
      }

      // Redirect to login or app choice page
      if (redirectAfterSignup) {
        navigate("/login?message=Please verify your email and log in");
      }
    } catch (error: any) {
      console.error("Agent signup failed:", error);

      let errorMessage = "Failed to create account. Please try again.";
      if (error.message?.includes("email")) {
        errorMessage =
          "This email is already registered. Try logging in instead.";
      } else if (error.message?.includes("User already registered")) {
        errorMessage =
          "This email is already registered. Try logging in instead.";
      }

      toast({
        variant: "destructive",
        title: "Signup Failed",
        description: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="w-full bg-white/5 backdrop-blur-xl border border-white/10 shadow-none rounded-2xl">
      <CardHeader className="relative pb-2">
        {onClose && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-4 top-4 rounded-full hover:bg-white/10 text-white/80 hover:text-white"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </Button>
        )}

        <div className="text-center pt-4">
          <div className="inline-flex items-center justify-center h-12 w-12 bg-purple-500/20 text-purple-400 rounded-full mb-4 mx-auto shadow-lg shadow-purple-500/25">
            <Users className="h-6 w-6" />
          </div>
          <CardTitle className="text-2xl font-semibold text-white mb-2">
            Join as an Agent
          </CardTitle>
          <CardDescription className="text-gray-300">
            Create your profile and start connecting with insurance agencies
          </CardDescription>
        </div>
      </CardHeader>

      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-4 px-8">
          {/* First Name */}
          <div className="space-y-2">
            <Label htmlFor="firstName" className="text-white/90 font-medium">
              First Name
            </Label>
            <Input
              id="firstName"
              type="text"
              value={formData.firstName}
              onChange={(e) => handleInputChange("firstName", e.target.value)}
              className={`bg-white/10 border-white/20 text-white placeholder:text-gray-400 focus:bg-white/15 focus:border-orange-400/50 rounded-xl h-12 transition-all duration-200 ${
                errors.firstName ? "border-red-500/50" : ""
              }`}
              placeholder="Enter your first name"
              disabled={isLoading}
            />
            {errors.firstName && (
              <p className="text-sm text-red-400">{errors.firstName}</p>
            )}
          </div>

          {/* Last Name */}
          <div className="space-y-2">
            <Label htmlFor="lastName" className="text-white/90 font-medium">
              Last Name
            </Label>
            <Input
              id="lastName"
              type="text"
              value={formData.lastName}
              onChange={(e) => handleInputChange("lastName", e.target.value)}
              className={`bg-white/10 border-white/20 text-white placeholder:text-gray-400 focus:bg-white/15 focus:border-orange-400/50 rounded-xl h-12 transition-all duration-200 ${
                errors.lastName ? "border-red-500/50" : ""
              }`}
              placeholder="Enter your last name"
              disabled={isLoading}
            />
            {errors.lastName && (
              <p className="text-sm text-red-400">{errors.lastName}</p>
            )}
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email" className="text-white/90 font-medium">
              Email Address
            </Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              className={`bg-white/10 border-white/20 text-white placeholder:text-gray-400 focus:bg-white/15 focus:border-orange-400/50 rounded-xl h-12 transition-all duration-200 ${
                errors.email ? "border-red-500/50" : ""
              }`}
              placeholder="Enter your email address"
              disabled={isLoading}
            />
            {errors.email && (
              <p className="text-sm text-red-400">{errors.email}</p>
            )}
          </div>

          {/* Password */}
          <div className="space-y-2">
            <Label htmlFor="password" className="text-white/90 font-medium">
              Password
            </Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={(e) => handleInputChange("password", e.target.value)}
                className={`bg-white/10 border-white/20 text-white placeholder:text-gray-400 focus:bg-white/15 focus:border-orange-400/50 rounded-xl h-12 transition-all duration-200 pr-12 ${
                  errors.password ? "border-red-500/50" : ""
                }`}
                placeholder="Create a password"
                disabled={isLoading}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-10 w-10 hover:bg-white/10 text-gray-400 hover:text-white rounded-lg"
                onClick={() => setShowPassword(!showPassword)}
                disabled={isLoading}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </Button>
            </div>
            {errors.password && (
              <p className="text-sm text-red-400">{errors.password}</p>
            )}
          </div>
        </CardContent>

        <CardFooter className="flex flex-col space-y-4 px-8 pb-8 pt-2">
          {/* Submit Button */}
          <Button
            type="submit"
            className="w-full h-12 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-medium rounded-xl shadow-lg shadow-orange-500/25 transition-all duration-200 disabled:opacity-50 disabled:shadow-none border-0"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating Account...
              </>
            ) : (
              "Create Agent Account"
            )}
          </Button>

          {/* Footer Links */}
          <div className="text-center text-sm text-gray-300 space-y-2">
            <p>
              Already have an account?{" "}
              <button
                type="button"
                className="text-orange-400 hover:text-orange-300 transition-colors duration-200 hover:underline font-medium"
                onClick={() => {
                  if (onSwitchToLogin) {
                    onClose?.();
                    onSwitchToLogin();
                  } else {
                    navigate("/login");
                  }
                }}
              >
                Sign in here
              </button>
            </p>
            <p>
              Want to create an agency?{" "}
              <button
                type="button"
                className="text-orange-400 hover:text-orange-300 transition-colors duration-200 hover:underline font-medium"
                onClick={() => {
                  onClose?.();
                  navigate("/pricing#plans");
                }}
              >
                Agency signup
              </button>
            </p>
          </div>
        </CardFooter>
      </form>
    </Card>
  );
}
